import React from "react";
import { FaCrown } from "react-icons/fa";

/**
 * Badges de rôle : pastilles compactes portées par les profils (barre du haut, carte Équipe du
 * dashboard, page Utilisateurs). Le verdict de DOMINANCE (quels rôles afficher) est calculé côté
 * serveur (RoleBadgeService) et arrive tout prêt via /api/me et /api/admin-users/directory ; ce
 * composant ne fait que rendre. La COURONNE identifie un « Responsable » (kind === "responsable")
 * pour économiser la place : même couleur que le contributeur du département, icône en plus.
 */

export interface RoleBadgeInfo {
	roleKey: string;
	label: string;
	/** Couleur hexadécimale stable du département/rôle (fond teinté + texte pleine couleur). */
	color: string;
	kind: "admin" | "responsable" | "contributeur" | "general" | string;
}

/** Une pastille : fond teinté à ~12 %, texte dans la couleur du rôle, couronne si responsable. */
export const RoleBadgeChip: React.FC<{ badge: RoleBadgeInfo; className?: string }> = ({ badge, className }) => (
	<span
		className={`inline-flex max-w-full items-center gap-1 truncate rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-none ${className ?? ""}`}
		style={{ backgroundColor: `${badge.color}1F`, color: badge.color }}
		title={badge.kind === "responsable" ? `Responsable : ${badge.label}` : badge.label}
	>
		{badge.kind === "responsable" && <FaCrown className="h-2.5 w-2.5 shrink-0" aria-label="Responsable" />}
		<span className="truncate">{badge.label}</span>
	</span>
);

/** Rangée de pastilles, bornée à `max` avec un compteur « +n » (les badges complets restent
 *  accessibles au survol du parent quand il fournit un popover). */
export const RoleBadgeList: React.FC<{ badges?: RoleBadgeInfo[] | null; max?: number; className?: string }> = ({
	badges,
	max = 3,
	className
}) => {
	const list = badges ?? [];
	if (list.length === 0) return null;
	const shown = list.slice(0, max);
	const overflow = list.length - shown.length;
	return (
		<span className={`inline-flex min-w-0 items-center gap-1 ${className ?? ""}`}>
			{shown.map((b) => (
				<RoleBadgeChip key={b.roleKey} badge={b} />
			))}
			{overflow > 0 && (
				<span className="inline-flex items-center rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-slate-500">
					+{overflow}
				</span>
			)}
		</span>
	);
};
